import { useGetHotelByUniqueIdQuery } from '@/src/api/rtkQuery/hotels/hotelReviewApi'
import { HOTEL_MAX_NIGHTS } from '@/src/constants/hotels'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { HotelByUniqueIdRequestParams, SelectHotelData } from '@/src/types/hotels/details'
import { getFormattedDate } from '@/src/utils/date'
import { PropertyStay } from '@/src/utils/hotels/propertyStay'
import usePropertyOccupancy from '@/src/hooks/hotels/usePropertyOccupancy'
import { transformHotelReview } from '@/src/utils/hotels/transformHotelReview'
import dayjs from 'dayjs'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useMemo, useState } from 'react'

/**
 * Data for the property (SEO) page.
 *
 * Deliberately separate from useHotelReviewData, which belongs to the booking flow and is
 * left untouched. The two pages load differently enough that sharing one hook meant
 * threading property-only options through booking-flow code:
 *
 *   booking flow   arrives from a search, already priced, and renders when the search lands
 *   property page  arrives from Google with no dates, renders server-side content first, and
 *                  asks upstream for prices only when the guest requests them
 *
 * The presentational components are shared; only the loading is not.
 */
interface UsePropertyPageDataArgs {
    /** Server-rendered content, shown until a search returns. */
    initialData: SelectHotelData
    /** Stay the page pre-filled, used when the guest asks for prices without changing dates. */
    initialStay: PropertyStay
    /** Required by the upstream search — the route carries no query string. */
    hotelName: string
    /** CT hotel id from the slug. */
    hotelId: string
}

const usePropertyPageData = ({ initialData, initialStay, hotelName, hotelId }: UsePropertyPageDataArgs) => {
    const locale = useLocale()
    const t = useTranslations('hotels')
    const { showError } = useThemedToast()

    // No search runs until the guest asks. A crawler hitting every property page must not put
    // a live upstream search behind each one.
    const [searchRequested, setSearchRequested] = useState(false)
    const [stay, setStay] = useState<PropertyStay>(initialStay)
    const occupancy = usePropertyOccupancy()

    const searchParams: HotelByUniqueIdRequestParams = {
        hotelId,
        hotelName,
        checkInDate: stay.checkInDate,
        checkOutDate: stay.checkOutDate,
        nights: String(stay.nights),
        ...occupancy.searchParams
    }

    const { data, isFetching, isError } = useGetHotelByUniqueIdQuery(searchParams, { skip: !searchRequested })

    // Static content is the floor: the moment the search returns, its payload replaces it
    // wholesale, so nothing is ever merged.
    const effectiveData = data ?? initialData
    const viewModel = useMemo(() => transformHotelReview(effectiveData, locale), [effectiveData, locale])

    /** Ask for prices with the stay already on screen. */
    const searchRooms = useCallback(() => setSearchRequested(true), [])

    /**
     * On this page a date change is the first search, not a re-price: checkHotelAvailability
     * needs a productId from a prior search, and there isn't one yet.
     */
    const applyDateChange = useCallback(
        async (checkIn: Date, checkOut: Date): Promise<boolean> => {
            const nights = dayjs(checkOut).startOf('day').diff(dayjs(checkIn).startOf('day'), 'day')
            if (nights < 1) return false
            if (nights > HOTEL_MAX_NIGHTS) {
                showError(t('maxNightsError', { nights: HOTEL_MAX_NIGHTS }))
                return false
            }

            setStay({
                checkInDate: getFormattedDate(checkIn, 'YYYY-MM-DD'),
                checkOutDate: getFormattedDate(checkOut, 'YYYY-MM-DD'),
                nights
            })
            setSearchRequested(true)
            return true
        },
        [showError, t]
    )

    return {
        viewModel,
        locale,
        searchRooms,
        occupancy,
        /** False until prices have been asked for. */
        hasSearched: searchRequested,
        // Content is always on screen, so a search in flight never shows a full-page loader.
        isLoading: false,
        isError: searchRequested && isError,
        isUpdatingDates: isFetching,
        applyDateChange
    }
}

export default usePropertyPageData
